/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import {
    type OnChanges,
    type OnDestroy,
    type OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { I18nService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { eventType, infigo } from '@dm/live-material-infigo';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DETAIL_LOADING_DELAY } from '../../const';
import { InfigoService } from '../../infigo.service';

@Component({
    selector: 'app-base-performace-chart',
    templateUrl: './base-performace-chart.component.html',
    styleUrls: ['./base-performace-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasePerformaceChartComponent implements OnInit, OnDestroy, OnChanges {
    @Input() id;
    @Input() type;
    @Input() performance: Array<any> = [];
    loading = true;
    status = false;
    abnormal = false;
    $destroy = new Subject<void>();
    performanceData: any = [];
    timeSegment = 'one_day';
    updateLoading = false;
    timer;
    timeOpts: SelectOption[] = [
        {
            label: this.i18n.get('home.search_term_day_lab'),
            value: 'one_day',
        },
        {
            label: this.i18n.get('home.search_term_week_lab'),
            value: 'one_week',
        },
        {
            label: this.i18n.get('home.search_term_month_lab'),
            value: 'one_month',
        },
    ];

    constructor(private infigoService: InfigoService,
        public i18n: I18nService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef) {
    }

    onTimeSegmentChange(value: string): void {
        this.timeSegment = value;
        this.getPerformance(false);
    }

    getPerformance(loading = true): void {
        this.timer = setTimeout(() => {
            this.updateLoading = true;
            this.changeDetectorRef.detectChanges();
        }, DETAIL_LOADING_DELAY);
        this.loading = loading;
        const performance = this.performance;
        this.infigoService.getHistoryPerformance(this.id, this.type, this.performance, this.timeSegment)
            .pipe(takeUntil(this.$destroy))
            .subscribe((res) => {
                const data = res.data;
                this.performanceData = data.sort((i, j) => performance.indexOf(i.indicator) - performance.indexOf(j.indicator));
                if (this.performanceData && this.performanceData.length === 0) {
                    this.performanceData = performance;
                }
                this.loading = false;
                this.clearTimer();
                this.updateLoading = false;
                this.changeDetectorRef.detectChanges();
            });
    }

    ngOnInit(): void {
        const data = infigo.getData(Symbol.for('PMS_LUN_STATUS'));
        if (!data) {
            return;
        }
        const { status, abnormal } = data;
        if (status) {
            this.status = true;
            this.getPerformance();
            return;
        }
        this.loading = false;
        this.abnormal = abnormal;
        this.changeDetectorRef.detectChanges();
    }

    clearTimer(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    ngOnChanges(changes): void {
        const { id: { firstChange, previousValue, currentValue } } = changes;
        if (!firstChange && previousValue !== currentValue) {
            if (this.status) {
                this.onTimeSegmentChange('one_day');
            }
        }
    }

    ngOnDestroy(): void {
        this.$destroy.next();
    }

    jumpToSetMonitor(): void {
        infigo.dispatch(eventType.CLICK_LIST_ITEM, { url: '/settings/monitor' });
    }
}
